import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import './SelfAssessment.css';

const Reflection = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // R1: Four Pillars Ranking
        pillar1: '',
        pillar2: '',
        pillar3: '',
        pillar4: '',
        // R2: Four Pillars Lifestyle Assessment
        breakfast: '',
        lunch: '',
        dinner: '',
        snacks: '',
        water: '',
        waterUnit: 'ml',
        bedTime: '',
        wakeTime: '',
        sleepQuality: '',
        typeOfExercise: '',
        workoutDaysPerWeek: '',
        minutesPerSession: '',
        stressors: '',
        stressManagement: '',
        // R3: Symptom Awareness
        symptomRelationAwareness: '',
        underlyingCausesAwareness: ''
    });
    const [saveStatus, setSaveStatus] = useState('');
    const [entryId, setEntryId] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });

    useEffect(() => {
        loadEntry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'reflection' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setEntryId(entry._id);
                setFormData(prev => ({ ...prev, ...entry.data }));
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
        } catch (error) {
            console.error('Error loading reflection entry:', error);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (Object.values(formData).some(val => val && val !== '')) {
                saveEntry();
            }
        }, 2000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    const saveEntry = async (includeStartTime = false) => {
        try {
            setSaveStatus('saving');
            const payload = {
                section: 'reflection',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        } catch (error) {
            console.error('Error saving reflection:', error);
            setSaveStatus('error');
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    // Get available pillar options for each ranking position
    const getAvailablePillarOptions = (currentPosition) => {
        const allOptions = ['Nutrition', 'Sleep', 'Exercise', 'Stress Management'];
        const selectedOptions = [];

        // Collect all selected options from other positions
        for (let i = 1; i <= 4; i++) {
            if (i !== currentPosition && formData[`pillar${i}`]) {
                selectedOptions.push(formData[`pillar${i}`]);
            }
        }

        // Return options that haven't been selected elsewhere
        return allOptions.filter(option => !selectedOptions.includes(option));
    };

    const isLocked = entryMetadata.lockedAt !== null;
    const modalConfig = getJournalStartConfig('reflection');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="self-assessment-container">
                {saveStatus && (
                    <div className={`save-status ${saveStatus}`}>
                        {saveStatus === 'saving' && '💾 Saving...'}
                        {saveStatus === 'saved' && '✓ Saved successfully'}
                        {saveStatus === 'error' && '⚠ Save failed'}
                    </div>
                )}

                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />

                <div className="journal-page-wrapper">
                    {/* R3: Symptom Awareness - NOW FIRST */}
                    <div className="section-header">
                        <h1 className="journal-title">Reflect</h1>
                    </div>

                    <div style={{ marginTop: '40px' }}>
                        <p className="journal-prompt">
                            Are you aware of how any of your symptoms may be related?
                        </p>

                        <div className="journal-line multi-line" style={{ marginTop: '30px' }}>
                            <textarea
                                value={formData.symptomRelationAwareness}
                                onChange={(e) => handleChange('symptomRelationAwareness', e.target.value)}
                                placeholder=""
                                rows={10}
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: '80px' }}>
                        <p className="journal-prompt">
                            Are you aware of the underlying causes of your symptoms?
                        </p>

                        <div className="journal-line multi-line" style={{ marginTop: '30px' }}>
                            <textarea
                                value={formData.underlyingCausesAwareness}
                                onChange={(e) => handleChange('underlyingCausesAwareness', e.target.value)}
                                placeholder=""
                                rows={10}
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* R2: Four Pillars Lifestyle - NOW SECOND */}
                    <div style={{ marginTop: '80px' }}>
                        <p className="journal-prompt">
                            Below are the four pillars of health. How does your lifestyle look right now?
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '40px' }}>
                            {/* Nutrition Column */}
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '25px' }}>Nutrition</h3>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Breakfast</label>
                                    <input
                                        type="text"
                                        value={formData.breakfast}
                                        onChange={(e) => handleChange('breakfast', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Lunch</label>
                                    <input
                                        type="text"
                                        value={formData.lunch}
                                        onChange={(e) => handleChange('lunch', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Dinner</label>
                                    <input
                                        type="text"
                                        value={formData.dinner}
                                        onChange={(e) => handleChange('dinner', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Snacks</label>
                                    <input
                                        type="text"
                                        value={formData.snacks}
                                        onChange={(e) => handleChange('snacks', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Water</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="number"
                                            value={formData.water}
                                            onChange={(e) => handleChange('water', e.target.value)}
                                            placeholder="0000"
                                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                            disabled={isLocked}
                                        />
                                        <select
                                            className='dropdown-style'
                                            value={formData.waterUnit}
                                            onChange={(e) => handleChange('waterUnit', e.target.value)}
                                            disabled={isLocked}
                                        >
                                            <option value="ml">ml</option>
                                            <option value="ounce">ounce</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Sleep Column */}
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '25px' }}>Sleep</h3>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Bed-time</label>
                                    <input
                                        type="time"
                                        value={formData.bedTime}
                                        onChange={(e) => handleChange('bedTime', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Wake-time</label>
                                    <input
                                        type="time"
                                        value={formData.wakeTime}
                                        onChange={(e) => handleChange('wakeTime', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '12px' }}>Sleep quality</label>
                                    {['Great!', 'Good', 'Okay', 'Poor', 'Terrible!'].map(option => (
                                        <div key={option} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <input
                                                type="radio"
                                                name="sleepQuality"
                                                value={option}
                                                checked={formData.sleepQuality === option}
                                                onChange={(e) => handleChange('sleepQuality', e.target.value)}
                                                style={{ marginRight: '10px' }}
                                                disabled={isLocked}
                                            />
                                            <span style={{ fontSize: '14px' }}>{option}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Exercise and Stress Management Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '40px' }}>
                            {/* Exercise Column */}
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '25px' }}>Exercise</h3>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Types of exercise</label>
                                    <input
                                        type="text"
                                        value={formData.typeOfExercise}
                                        onChange={(e) => handleChange('typeOfExercise', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>No. of work-out days per week</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="7"
                                        value={formData.workoutDaysPerWeek}
                                        onChange={(e) => handleChange('workoutDaysPerWeek', e.target.value)}
                                        placeholder="0"
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>Minutes of exercise per session</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.minutesPerSession}
                                        onChange={(e) => handleChange('minutesPerSession', e.target.value)}
                                        placeholder="000"
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>

                            {/* Stress Management Column */}
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '25px' }}>Stress management</h3>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>What are your stressors?</label>
                                    <input
                                        type="text"
                                        value={formData.stressors}
                                        onChange={(e) => handleChange('stressors', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="journal-line" style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>How do you manage stress?</label>
                                    <input
                                        type="text"
                                        value={formData.stressManagement}
                                        onChange={(e) => handleChange('stressManagement', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* R1: Four Pillars Ranking - NOW THIRD */}
                    <div style={{ marginTop: '80px' }}>
                        <p className="journal-prompt">
                            Rank the four pillars that you would like to work on in order of importance
                        </p>

                        <div className="four-pillars-header">
                            <div>
                                <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '15px' }}>Nutrition</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '15px' }}>Sleep</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '15px' }}>Exercise</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '15px' }}>Stress management</p>
                            </div>
                        </div>

                        <div style={{ marginTop: '40px' }}>
                            {[1, 2, 3, 4].map(num => {
                                const availableOptions = getAvailablePillarOptions(num);
                                return (
                                    <div key={num} style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                        <strong style={{ fontSize: '16px', marginRight: '15px', minWidth: '20px' }}>{num}.</strong>
                                        <div className="journal-line" style={{ flex: 1 }}>
                                            <select
                                                value={formData[`pillar${num}`]}
                                                onChange={(e) => handleChange(`pillar${num}`, e.target.value)}
                                                disabled={isLocked}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 0',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    borderBottom: 'none',
                                                    fontSize: '16px',
                                                    color: '#2c2416',
                                                    outline: 'none',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="">Select a pillar...</option>
                                                {availableOptions.map(option => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '40px', lineHeight: '1.7', textAlign: 'justify' }}>
                            Great! The pillar that you've selected for no. 1 will now be referred to as your 'non-negotiable!' This means that if life gets unpredictable, and it becomes challenging to maintain all your pillars of health, then you will focus solely on your 'non-negotiable' as best as you can. You've got this!
                        </p>

                        <JournalNavigation
                            prevPage="/journal/self-assessment"
                            nextPage="/journal/transformation"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Reflection;
